import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { LuceneExpression, toString as luceneToString } from 'lucene';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { Batch } from '../../models/batch';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { BatchesService } from '../../services/batches.service';
import { OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { GROSS } from '../../../shared/models/settings';
import { DatacenterFilterType } from '../../../shared/models/datacenter-filters';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';

const viewDetailsFilter = {
	id: 'statusGroup',
	values: [
		[
			{
				apiName: 'statusGroup',
				selected: false,
				value: '0'
			},
			{
				apiName: 'statusGroup',
				selected: false,
				value: '1'
			},
			{
				apiName: 'statusGroup',
				selected: false,
				value: '2'
			},
			{
				apiName: 'statusGroup',
				selected: true,
				value: '3'
			},
			{
				apiName: 'statusGroup',
				selected: false,
				value: '4'
			},
			{
				apiName: 'statusGroup',
				selected: false,
				value: '5'
			}
		]
	]
};

const inLandTransitFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '30', translation: 'Land Transit' }]
};

const atReshipperFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '31', translation: 'At Reschipper' }]
};

const onVesselFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '32', translation: 'On Vessel' }]
};

const atOverseasStockFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '33', translation: 'Overseas Stock' }]
};

const inOverseasTransitFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '34', translation: 'In Overseas Transit' }]
};

const inTransitToCustomerFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '35', translation: 'In Transit To Customer' }]
};

const atConsignmentFilter: DatacenterFilterType = {
	id: 'statusCategory',
	values: [{ code: '40', translation: 'In Consignment' }]
};

interface FollowUpItem {
	qty: number;
	filter: DatacenterFilterType;
}

const batchesFields = <const> [
	'qtyBatchNet',
	'qtyBatchGross',
	'batchStatusCategory',
	'batchStatusCategoryDescription'
];

const statusArray = ['30', '31', '32', '33', '34', '35', '40'];

const nameStatus = 'batchStatusCategory';

@Component({
	selector: 'copo-delivery-follow-up',
	templateUrl: './delivery-follow-up.component.html',
	styleUrl: './delivery-follow-up.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,
		MatRippleModule,
		MatIconModule,

		MatButtonModule,
		LoadingSpinnerComponent,
		NumberPipe
	]
})
export class DeliveryFollowUpComponent implements OnInit, OnDestroy {
	private static createLuceneFilter() {
		let statusCondition: LuceneExpression;
		if (statusArray.length === 1) {
			statusCondition = {
				field: nameStatus,
				term: statusArray[0],
				quoted: false
			};
		} else {
			statusCondition = {
				left: {
					field: nameStatus,
					term: statusArray[0],
					quoted: false
				},
				operator: 'OR',
				right: {
					field: nameStatus,
					term: statusArray[1],
					quoted: false
				},
				parenthesized: false
			};
			let current = statusCondition;
			for (const status of statusArray.slice(2)) {
				current.right = {
					left: current.right,
					operator: 'OR',
					right: {
						field: nameStatus,
						term: status,
						quoted: false
					},
					parenthesized: false
				};
				current = current.right;
			}
		}
		return luceneToString(statusCondition);
	}

	isLoading: boolean;
	weightDisplay?: string;
	batchesSub?: Subscription;
	deliveryTrackingResults?: PaginatedResult<Batch>;
	inLandTransit: FollowUpItem = {
		qty: 0,
		filter: inLandTransitFilter,
	};

	atReshipper: FollowUpItem = {
		qty: 0,
		filter: atReshipperFilter,
	};

	onVessel: FollowUpItem = {
		qty: 0,
		filter: onVesselFilter,
	};

	atOverseasStock: FollowUpItem = {
		qty: 0,
		filter: atOverseasStockFilter,
	};

	inOverseasTransit: FollowUpItem = {
		qty: 0,
		filter: inOverseasTransitFilter,
	};

	inTransitToCustomer: FollowUpItem = {
		qty: 0,
		filter: inTransitToCustomerFilter,
	};

	atConsignment: FollowUpItem = {
		qty: 0,
		filter: atConsignmentFilter,
	};

	constructor(
		private readonly batchesService: BatchesService,
		private readonly router: Router
	) {
		this.isLoading = true;
	}

	ngOnInit() {
		this.batchesSub = this.batchesService.getBatches(
			batchesFields,
			DeliveryFollowUpComponent.createLuceneFilter(),
			0,
			100,
			new OrderBy(nameStatus, OrderByDirection.ASC),
			true
		)
			.subscribe({
				next: deliveryTrackingResults => {
					this.deliveryTrackingResults = deliveryTrackingResults;
					this.constructData();
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	constructData() {
		if (!this.deliveryTrackingResults) {
			return;
		}
		for (const deliveryTracking of this.deliveryTrackingResults.results) {
			switch (deliveryTracking.batchStatusCategory) {
			case '30':
				this.inLandTransit.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			case '31':
				this.atReshipper.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			case '32':
				this.onVessel.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			case '33':
				this.atOverseasStock.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			case '34':
				this.inOverseasTransit.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			case '35':
				this.inTransitToCustomer.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			case '40':
				this.atConsignment.qty = this.weightDisplay === GROSS
					? deliveryTracking.qtyBatchGross
					: deliveryTracking.qtyBatchNet;
				break;
			default:
				break;
			}
		}
	}

	ngOnDestroy() {
		if (this.batchesSub) {
			this.batchesSub.unsubscribe();
		}
	}

	async viewDetails(followUpItem?: FollowUpItem) {
		if (followUpItem && followUpItem.qty === 0) {
			return;
		}
		await this.router.navigateByUrl('/order-tracking/batch/datacenter', {
			state: {
				filter: [!followUpItem ? viewDetailsFilter : followUpItem.filter],
				columnId: !followUpItem ? 'statusGroupDescription' : 'batchStatusCategoryDescription'
			}
		});
	}
}
